// @migration-in-progress
import React from 'react';
import { VESSEL_TYPE_LABELS, VESSEL_CATEGORY_LABELS, getAvailableVesselTypes, getVesselTypeLabel } from '../../../../config/vesselTypes';
import * as AutoConfig from './utils/vesselAutoConfig';
import EnginePropulsionSection from './components/EnginePropulsionSection';
import { COMMON_TIMEZONES, getOffsetForTimezone } from '../../../../utils/timezoneUtils';
// import EquipmentRequirementsPreview from '../EquipmentRequirementsPreview'; // Removed - old test function no longer used

const BasicInfoTab = ({ vesselConfig, updateConfig }) => {
  // FT-269: detected device timezone, used as the "Use device timezone" default option.
  const detectedTimezone = (typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'UTC') || 'UTC';

  // FT-269 Phase B.1: IANA timezone validation (defence-in-depth).
  // The picker below is a dropdown sourced from a known IANA list, so
  // validation is implicit at the UI layer. We still validate here so that
  // any future free-text reveal, programmatic update, or corrupted persisted
  // value can never push a bad IANA name into vesselConfig.timezone.
  const isValidIanaTimezone = (candidate) => {
    if (!candidate) return true; // empty = "use device timezone" — valid
    try {
      // eslint-disable-next-line no-new
      new Intl.DateTimeFormat('en-GB', { timeZone: candidate });
      return true;
    } catch (err) {
      return false;
    }
  };
  const [timezoneError, setTimezoneError] = React.useState(null);
  const handleTimezoneChange = (value) => {
    if (!isValidIanaTimezone(value)) {
      setTimezoneError('Not a valid timezone (e.g. "Europe/London")');
      return; // do NOT save invalid candidate
    }
    setTimezoneError(null);
    updateConfig({ timezone: value });
  };
  const showPassengerAlert = vesselConfig.requiresPassengerInput && (!vesselConfig.passengerCapacity || vesselConfig.passengerCapacity === 0 || vesselConfig.passengerCapacity === '');

  return (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    {/* Main configuration form - 2/3 width on desktop */}
    <div className="lg:col-span-2 space-y-6">
    {showPassengerAlert && (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Passenger Capacity Required</h3>
            <p className="mt-2 text-sm text-yellow-700">{vesselConfig.passengerInputReminder}</p>
          </div>
        </div>
      </div>
    )}

    <div>
      <h3 className="text-base font-semibold text-gray-900">Vessel Identification</h3>
      <p className="mt-1 text-sm text-gray-600">Basic vessel information and classification</p>
    </div>

    <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
      <div className="sm:col-span-3">
        <label htmlFor="name" className="block text-sm font-medium text-gray-900">Vessel Name <span className="text-red-500">*</span></label>
        <div className="mt-2">
          <input
            type="text"
            id="name"
            value={vesselConfig.vesselName}
            onChange={(e) => updateConfig({ vesselName: e.target.value })}
            placeholder="e.g., MV Example"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>

      {/* FT-269: Vessel timezone picker. Stored as IANA name; voyage events
          snapshot the numeric offset at write time so display survives
          later vessel-tz changes. Empty string = device default at render. */}
      <div className="sm:col-span-3">
        <label htmlFor="timezone" className="block text-sm font-medium text-gray-900">Timezone</label>
        <div className="mt-2">
          <select
            id="timezone"
            value={vesselConfig.timezone || ''}
            onChange={(e) => handleTimezoneChange(e.target.value)}
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          >
            <option value="">Use device timezone ({detectedTimezone} {getOffsetForTimezone(detectedTimezone) ? `GMT${getOffsetForTimezone(detectedTimezone).slice(0, 3)}${getOffsetForTimezone(detectedTimezone).slice(3) === '00' ? '' : `:${getOffsetForTimezone(detectedTimezone).slice(3)}`}` : ''})</option>
            {COMMON_TIMEZONES.map(tz => {
              const offset = getOffsetForTimezone(tz);
              const sign = offset[0];
              const hh = parseInt(offset.slice(1, 3), 10);
              const mm = offset.slice(3, 5);
              const label = tz === 'UTC' ? 'UTC' : `${tz} (GMT${sign}${hh}${mm === '00' ? '' : `:${mm}`})`;
              return <option key={tz} value={tz}>{label}</option>;
            })}
          </select>
        </div>
        {timezoneError && (
          <p className="mt-1 text-xs text-red-600" role="alert">{timezoneError}</p>
        )}
        <p className="mt-1 text-xs text-gray-500">Voyage events display in this timezone; UTC is stored under the hood.</p>
      </div>

      <div className="sm:col-span-3">
        <label htmlFor="type" className="block text-sm font-medium text-gray-900">Vessel Type <span className="text-red-500">*</span></label>
        <div className="mt-2">
          <select
            id="type"
            value={vesselConfig.vesselType}
            onChange={(e) => AutoConfig.handleVesselTypeChange(e.target.value, vesselConfig, updateConfig)}
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          >
            <option value="">Select type...</option>
            {getAvailableVesselTypes(vesselConfig.operatingStatus).map((value) => (
              <option key={value} value={value}>{getVesselTypeLabel(value, vesselConfig.operatingStatus)}</option>
            ))}
          </select>
        </div>
      </div>

      {vesselConfig.operatingStatus !== 'inland' && (
        <div className="sm:col-span-2">
          <label htmlFor="category" className="block text-sm font-medium text-gray-900">WBC3 Category <span className="text-red-500">*</span></label>
          <div className="mt-2">
            <select
              id="category"
              value={vesselConfig.category}
              onChange={(e) => AutoConfig.handleCategoryChange(e.target.value, vesselConfig, updateConfig)}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="">Select category...</option>
              {Object.entries(VESSEL_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">Determines operational limits</p>
        </div>
      )}

      <div className="sm:col-span-2">
        <label htmlFor="operatingHours" className="block text-sm font-medium text-gray-900">Operating Hours</label>
        <div className="mt-2">
          <select
            id="operatingHours"
            value={vesselConfig.operatingHours}
            onChange={(e) => AutoConfig.handleOperatingHoursChange(e.target.value, vesselConfig, updateConfig)}
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          >
            <option value="under_24h">Under 24 hours</option>
            <option value="over_24h">Over 24 hours</option>
          </select>
        </div>
        <p className="mt-1 text-xs text-gray-500">Affects manning requirements</p>
      </div>
      
      {vesselConfig.operatingStatus === 'seagoing' && (
        <div className="sm:col-span-2">
          <label htmlFor="seaArea" className="block text-sm font-medium text-gray-900">GMDSS Sea Area</label>
          <div className="mt-2">
            <select
              id="seaArea"
              value={vesselConfig.gmdssSeaArea || vesselConfig.seaArea || 'A1'}
              onChange={(e) => {
                const area = e.target.value;
                // Set the appropriate sea area flags based on cumulative coverage
                // Each higher area includes all lower areas
                updateConfig({
                  gmdssSeaArea: area, // Primary field (used by wizard)
                  seaArea: area, // Legacy compatibility
                  seaAreaA1: true, // All vessels operate in A1
                  seaAreaA2: area === 'A2' || area === 'A3' || area === 'A4',
                  seaAreaA3: area === 'A3' || area === 'A4',
                  seaAreaA4: area === 'A4'
                });
              }}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="A1">A1 - Coastal (up to 30nm, VHF only)</option>
              <option value="A2">A1+A2 - Offshore (up to 150nm, VHF+MF)</option>
              <option value="A3">A1+A2+A3 - Ocean (70°N to 70°S, VHF+MF+Satellite)</option>
              <option value="A4">A1+A2+A3+A4 - Worldwide (inc. polar regions)</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">Determines GMDSS equipment requirements</p>
        </div>
      )}

      <div className="sm:col-span-2">
        <label htmlFor="operatingStatus" className="block text-sm font-medium text-gray-900">Vessel Operating Status <span className="text-red-500">*</span></label>
        <div className="mt-2">
          <select
            id="operatingStatus"
            value={vesselConfig.operatingStatus || ''}
            onChange={(e) => {
              const newStatus = e.target.value;
              const currentStatus = vesselConfig.operatingStatus;
              
              // Show warning when changing between seagoing and inland
              if (currentStatus && newStatus && currentStatus !== newStatus) {
                const isChangingToInland = newStatus === 'inland';
                const isChangingToSeagoing = newStatus === 'seagoing';
                
                if (isChangingToInland || isChangingToSeagoing) {
                  // Check if current vessel type will become invalid
                  const availableTypes = getAvailableVesselTypes(newStatus);
                  const willResetVesselType = vesselConfig.vesselType && !availableTypes.includes(vesselConfig.vesselType);
                  
                  let message = isChangingToInland 
                    ? "Changing to inland waterways will update maintenance requirements to use inland regulations (COP13, MSN 1823, AINA Code) instead of WBC3. Special operations and equipment will remain but regulatory references will change."
                    : "Changing to seagoing will update maintenance requirements to use WBC3 regulations instead of inland waterways codes. Special operations and equipment will remain but regulatory references will change.";
                  
                  if (willResetVesselType) {
                    message += `\n\n⚠️ Your current vessel type (${VESSEL_TYPE_LABELS[vesselConfig.vesselType]}) is not available for ${newStatus} operations and will be reset.`;
                  }
                  
                  message += "\n\nDo you want to continue?";
                  
                  if (!window.confirm(message)) {
                    return; // Cancel the change
                  }
                  
                  // Reset vessel type if it's not available in new operating status
                  if (willResetVesselType) {
                    updateConfig({ 
                      operatingStatus: newStatus,
                      vesselType: '',
                      // Reset dependent fields
                      operations: [],
                      specialEquipment: []
                    });
                    return;
                  }
                }
              }
              
              updateConfig({ operatingStatus: newStatus });
            }}
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          >
            <option value="">Select status...</option>
            <option value="seagoing">Seagoing Vessel</option>
            <option value="inland">Inland Waterway Vessel</option>
          </select>
        </div>
        <p className="mt-1 text-xs text-gray-500">Select based on vessel certification and operating area</p>
      </div>

      {vesselConfig.operatingStatus === 'inland' && (
        <div className="sm:col-span-2">
          <label htmlFor="waterCategory" className="block text-sm font-medium text-gray-900">
            Water Category <span className="text-red-500">*</span>
          </label>
          <div className="mt-2">
            <select
              id="waterCategory"
              value={vesselConfig.waterCategory || ''}
              onChange={(e) => updateConfig({ waterCategory: e.target.value })}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="">Select category...</option>
              <option value="A">Category A - Narrow rivers/canals (&lt;1.5m depth)</option>
              <option value="B">Category B - Wider waters (≥1.5m depth, waves &lt;0.6m)</option>
              <option value="C">Category C - Tidal waters (waves &lt;1.2m)</option>
              <option value="D">Category D - Tidal waters (waves &lt;2.0m)</option>
            </select>
          </div>
          <p className="mt-1 text-xs text-gray-500">As per MSN 1837 categorisation</p>
        </div>
      )}

      {vesselConfig.operatingStatus === 'inland' && (
        <div className="sm:col-span-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Inland Operations</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={vesselConfig.carriesVulnerablePassengers || false}
                  onChange={(e) => updateConfig({ carriesVulnerablePassengers: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">Carries Vulnerable Passengers</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">Affects lifejacket requirements in Cat A/B waters</p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={vesselConfig.operatesAtNight || false}
                  onChange={(e) => updateConfig({ operatesAtNight: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">Night Operations</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">Requires lights on lifejackets per COP13 13.2.4</p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={vesselConfig.allPersonsWearLifejackets || false}
                  onChange={(e) => updateConfig({ allPersonsWearLifejackets: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">All Persons Wear Lifejackets</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">Exempts lifebuoy requirement per COP13 13.1.4</p>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={vesselConfig.hasEnclosedBridge || false}
                  onChange={(e) => updateConfig({ hasEnclosedBridge: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">Enclosed Bridge/Wheelhouse</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">Requires sound reception equipment in Cat 0-2 per WBC3 19.8</p>
            </div>

          </div>
        </div>
      )}

      {vesselConfig.operatingStatus === 'inland' && vesselConfig.vesselType === 'hire_self_drive' && (
        <div className="sm:col-span-6">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Regulatory Compliance Path</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label htmlFor="inlandRegulatoryPath" className="block text-sm font-medium text-gray-900 mb-2">
              Choose Regulatory Framework <span className="text-red-500">*</span>
            </label>
            <select
              id="inlandRegulatoryPath"
              value={vesselConfig.inlandRegulatoryPath || 'hire_boat_code'}
              onChange={(e) => updateConfig({ inlandRegulatoryPath: e.target.value })}
              className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
            >
              <option value="hire_boat_code">AINA Hire Boat Code (Recommended)</option>
              <option value="class_ix_a">Full Class IX(A) Regulations</option>
            </select>
            <div className="mt-3 text-xs text-gray-600">
              {vesselConfig.inlandRegulatoryPath === 'hire_boat_code' ? (
                <>
                  <p className="font-medium text-green-700">✓ AINA Hire Boat Code 2021 (General Exemption)</p>
                  <p>• Practical safety standards for hire boats</p>
                  <p>• Risk assessments and safety equipment required</p>
                  <p>• General exemption from full Class IX(A) requirements</p>
                  <p>• Turn-round checks every hire period</p>
                  <p>• Weekly periodic inspections</p>
                  <p>• Annual major servicing requirements</p>
                </>
              ) : (
                <>
                  <p className="font-medium text-orange-700">⚠ Full Class IX(A) Regulations</p>
                  <p>• Complete merchant shipping LSA requirements (SI 2721/1999)</p>
                  <p>• Full fire fighting equipment requirements (SI 1011/1998)</p>
                  <p>• MCA inspection powers apply</p>
                  <p>• Higher compliance burden but comprehensive</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="sm:col-span-2">
        <label htmlFor="lengthOverall" className="block text-sm font-medium text-gray-900">Length Overall (m)</label>
        <div className="mt-2">
          <input
            type="number"
            id="lengthOverall"
            value={vesselConfig.lengthOverall}
            onChange={(e) => AutoConfig.handleLengthChange(e.target.value, vesselConfig, updateConfig)}
            placeholder="e.g., 24.5"
            step="0.1"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900">Dimensions & Capacity</h3>
      <p className="mt-1 text-sm text-gray-600">Physical specifications and capacity</p>
    </div>

    <div className="grid grid-cols-1 gap-6 sm:grid-cols-6">
      <div className="sm:col-span-2">
        <label htmlFor="grossTonnage" className="block text-sm font-medium text-gray-900">Gross Tonnage</label>
        <div className="mt-2">
          <input
            type="number"
            id="grossTonnage"
            value={vesselConfig.grossTonnage}
            onChange={(e) => updateConfig({ grossTonnage: e.target.value ? parseFloat(e.target.value) : '' })}
            placeholder="e.g., 250"
            step="0.01"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="draft" className="block text-sm font-medium text-gray-900">Draft (m)</label>
        <div className="mt-2">
          <input
            type="number"
            id="draft"
            value={vesselConfig.draft}
            onChange={(e) => updateConfig({ draft: e.target.value })}
            placeholder="e.g., 2.5"
            step="0.1"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="enginePower" className="block text-sm font-medium text-gray-900">Engine Power (kW)</label>
        <div className="mt-2">
          <input
            type="number"
            id="enginePower"
            value={vesselConfig.enginePower}
            onChange={(e) => AutoConfig.handleEnginePowerChange(e.target.value, vesselConfig, updateConfig)}
            placeholder="e.g., 500"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="maxSpeed" className="block text-sm font-medium text-gray-900">Maximum Speed (knots)</label>
        <div className="mt-2">
          <input
            type="number"
            id="maxSpeed"
            value={vesselConfig.maxSpeed}
            onChange={(e) => AutoConfig.handleMaxSpeedChange(e.target.value, vesselConfig, updateConfig)}
            placeholder="e.g., 25"
            min="0"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">≥20 knots triggers high-speed requirements</p>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="age" className="block text-sm font-medium text-gray-900">Vessel Age (years)</label>
        <div className="mt-2">
          <input
            type="number"
            id="age"
            value={vesselConfig.age}
            onChange={(e) => updateConfig({ age: e.target.value })}
            placeholder="e.g., 5"
            min="0"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">Affects survey requirements</p>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="crewCount" className="block text-sm font-medium text-gray-900">Crew Count</label>
        <div className="mt-2">
          <input
            type="number"
            id="crewCount"
            value={vesselConfig.crewCount}
            onChange={(e) => updateConfig({ crewCount: e.target.value })}
            placeholder="e.g., 3"
            min="1"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="maxPersons" className="block text-sm font-medium text-gray-900">Maximum Persons</label>
        <div className="mt-2">
          <input
            type="number"
            id="maxPersons"
            value={vesselConfig.maxPersons}
            onChange={(e) => AutoConfig.handleMaxPersonsChange(e.target.value, vesselConfig, updateConfig)}
            placeholder="e.g., 12"
            className="block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm"
          />
        </div>
      </div>

      <div className="sm:col-span-2">
        <label htmlFor="passengerCapacity" className="block text-sm font-medium text-gray-900">
          Passenger Capacity
          {vesselConfig.requiresPassengerInput && (
            <span className="ml-2 text-red-500">*</span>
          )}
        </label>
        <div className="mt-2">
          <input
            type="number"
            id="passengerCapacity"
            value={vesselConfig.passengerCapacity}
            onChange={(e) => AutoConfig.handlePassengerCapacityChange(e.target.value, vesselConfig, updateConfig)}
            placeholder={vesselConfig.requiresPassengerInput ? "Required" : "e.g., 0"}
            className={`block w-full rounded-md px-3 py-1.5 text-gray-900 outline outline-1 -outline-offset-1 ${
              vesselConfig.requiresPassengerInput && !vesselConfig.passengerCapacity
                ? 'outline-red-500 bg-red-50'
                : 'outline-gray-300'
            } placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600 sm:text-sm`}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {vesselConfig.passengerInputReminder || 'Non-crew passengers only'}
        </p>
      </div>
    </div>

    <EnginePropulsionSection vesselConfig={vesselConfig} updateConfig={updateConfig} />
    </div>
    
    {/* Equipment Requirements Preview removed - old test function no longer used */}
  </div>
  );
};

export default BasicInfoTab;